import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.slug === id);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的内容不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '国产精品在线',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${article.title} - 国产精品在线`,
    description: article.excerpt,
    url: `https://www.guochangjingpin.com/article/${article.slug}`,
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.guochangjingpin.com/article/${article.slug}`}
        ogType="article"
        schemaData={[articleSchema, webPageSchema]}
      />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: category?.name || '分类', path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        {/* Article Header */}
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                {category?.name}
              </span>
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
              <span className="text-xs text-foreground-500">{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>

            <div className="flex flex-wrap gap-1.5 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 国产精品在线`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
              prose-p:mb-4 prose-p:leading-relaxed
              prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg prose-h3:font-heading prose-h3:font-semibold prose-h3:mt-6 prose-h3:mb-3"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <div className="border-t border-background-200/70 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
            <Link
              to={`/category/${article.category}`}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
            >
              查看更多{category?.name}内容 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relatedArticles.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer hover:border-primary-300 transition-all duration-300"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{item.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}